import React, { useState, useEffect } from 'react';
import { Wrench, Zap, Cpu, Compass, BookOpen, Play, CheckCircle2, Layers, Clock, ArrowLeft, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/Card';
import { TrackSummary, TrackType, Category } from '../../shared/types';
import { api } from '../lib/api';

export interface TrackPageProps {
  trackSlug: string;
  onNavigate: (path: string) => void;
  onStartStudy: (track: TrackType, categoryId?: string, questionCount?: number) => void;
  onStartMock: (track: TrackType, categoryId?: string, questionCount?: number) => void;
}

export function TrackPage({
  trackSlug,
  onNavigate,
  onStartStudy,
  onStartMock,
}: TrackPageProps) {
  const [trackData, setTrackData] = useState<TrackSummary | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string | undefined>(undefined);
  const [questionCount, setQuestionCount] = useState<number>(30);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function loadTrack() {
      try {
        setIsLoading(true);
        const data = await api.tracks.getOne(trackSlug);
        setTrackData(data);
      } catch (err) {
        console.error('Failed to load track', err);
      } finally {
        setIsLoading(false);
      }
    }
    loadTrack();
  }, [trackSlug]);

  if (isLoading || !trackData) {
    return (
      <div className="container max-w-5xl py-16 text-center">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-amber-500 border-t-transparent" />
        <p className="text-sm text-slate-400 mt-4">Loading track details...</p>
      </div>
    );
  }

  const getIcon = () => {
    switch (trackData.track) {
      case 'mechanical': return <Wrench className="h-8 w-8 text-amber-400" />;
      case 'electrical': return <Zap className="h-8 w-8 text-sky-400" />;
      case 'multicraft': return <Cpu className="h-8 w-8 text-emerald-400" />;
      case 'aptitude': return <Compass className="h-8 w-8 text-purple-400" />;
    }
  };

  return (
    <div className="container max-w-5xl py-8 space-y-8 pb-16">
      {/* Back Link */}
      <a
        href="/"
        onClick={(e) => {
          if (!e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey && e.button === 0) {
            e.preventDefault();
            onNavigate('/');
          }
        }}
        className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden="true" />
        Back to All Tracks
      </a>

      {/* Header Banner */}
      <div className="rounded-2xl border border-border dark:border-slate-800 bg-card/80 dark:bg-slate-900/80 p-8 relative overflow-hidden backdrop-blur-md shadow-sm">
        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
          <div className="flex items-start gap-4">
            <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shrink-0">
              {getIcon()}
            </div>
            <div>
              <Badge variant={trackData.track as any} className="mb-1.5">
                {trackData.track} Assessment Track
              </Badge>
              <h1 className="text-2xl md:text-3xl font-extrabold text-foreground dark:text-white tracking-tight">
                {trackData.title}
              </h1>
              <p className="text-sm text-slate-600 dark:text-slate-300 mt-2 max-w-2xl leading-relaxed">
                {trackData.description}
              </p>
            </div>
          </div>

          <div className="flex flex-col gap-3 w-full lg:w-auto shrink-0">
            {/* Question count selector */}
            <div className="flex items-center gap-2 lg:justify-end text-xs">
              <span className="text-muted-foreground font-medium">Exam Size:</span>
              <div className="inline-flex rounded-lg border border-border dark:border-slate-700 bg-background/80 p-0.5">
                {[10, 20, 30, 60].map((count) => (
                  <button
                    key={count}
                    onClick={() => setQuestionCount(count)}
                    className={`px-2.5 py-1 text-xs font-semibold rounded-md transition-all ${
                      questionCount === count
                        ? 'bg-amber-500 text-slate-950 shadow-sm'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    {count} Qs ({count}m)
                  </button>
                ))}
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                variant="secondary"
                size="md"
                onClick={() => onStartStudy(trackData.track, selectedCategory, questionCount)}
                className="gap-2 text-sm shadow-sm"
              >
                <BookOpen className="h-4 w-4 text-amber-500 dark:text-amber-400" />
                Study Mode
              </Button>
              <Button
                variant="amber"
                size="md"
                onClick={() => onStartMock(trackData.track, selectedCategory, questionCount)}
                className="gap-2 text-sm shadow-md shadow-amber-500/20"
              >
                <Play className="h-4 w-4 fill-current" />
                Timed Mock ({questionCount}m)
              </Button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-8 pt-6 border-t border-border dark:border-slate-800 text-xs">
          <div>
            <span className="text-muted-foreground block">Available Questions</span>
            <span className="font-bold text-foreground dark:text-white text-base font-mono">{trackData.totalQuestions} Questions</span>
          </div>
          <div>
            <span className="text-muted-foreground block">Sub-Categories</span>
            <span className="font-bold text-foreground dark:text-white text-base font-mono">{trackData.categories.length} Modules</span>
          </div>
          <div>
            <span className="text-muted-foreground block">Pacing Standard</span>
            <span className="font-bold text-amber-600 dark:text-amber-400 text-base font-mono">1 Min / Question</span>
          </div>
          <div>
            <span className="text-muted-foreground block">Passing Benchmark</span>
            <span className="font-bold text-emerald-600 dark:text-emerald-400 text-base font-mono">70% Accuracy</span>
          </div>
        </div>
      </div>

      {/* Featured Electrical Schematics Course Banner */}
      {trackData.track === 'electrical' && (
        <div className="rounded-2xl border border-sky-500/30 bg-gradient-to-r from-sky-50 via-card to-amber-50/50 dark:from-sky-950/70 dark:via-slate-900 dark:to-amber-950/30 p-6 flex flex-col sm:flex-row items-center justify-between gap-5 shadow-sm">
          <div className="flex items-center gap-4">
            <div className="h-12 w-12 rounded-xl bg-sky-500/20 border border-sky-500/40 flex items-center justify-center shrink-0">
              <BookOpen className="h-6 w-6 text-sky-600 dark:text-sky-400" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-extrabold px-2 py-0.5 rounded bg-sky-500/20 text-sky-700 dark:text-sky-300 uppercase tracking-wider">
                  Featured Core Curriculum
                </span>
                <span className="text-xs font-semibold text-amber-700 dark:text-amber-400">Industrial Electrical Curriculum</span>
              </div>
              <h2 className="text-base font-bold text-foreground mt-1">
                Complete Electrical Schematics &amp; Blueprint Reading Course
              </h2>
              <p className="text-xs text-muted-foreground mt-0.5 max-w-xl">
                Master schematic symbols, active/passive components, signal tracing across multi-page blueprints, and component-level diagnostic procedures. Includes 5 interactive simulators and a 30-question final exam.
              </p>
            </div>
          </div>
          <Button
            variant="amber"
            size="md"
            onClick={() => onNavigate('/course/schematics')}
            className="text-xs font-bold gap-2 shrink-0 whitespace-nowrap shadow-md shadow-amber-500/20"
          >
            Launch Schematics Course
            <ArrowRight className="h-4 w-4" />
          </Button>
        </div>
      )}

      {/* Category Breakdown */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-foreground dark:text-white tracking-tight flex items-center gap-2">
            <Layers className="h-5 w-5 text-amber-500 dark:text-amber-400" />
            Core Assessment Modules
          </h2>
          {selectedCategory && (
            <button
              onClick={() => setSelectedCategory(undefined)}
              className="text-xs text-amber-600 dark:text-amber-400 hover:underline"
            >
              Clear Filter (All Categories)
            </button>
          )}
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          {trackData.categories.map((category) => {
            const isSelected = selectedCategory === category.id;
            return (
              <Card
                key={category.id}
                onClick={() => setSelectedCategory(isSelected ? undefined : category.id)}
                className={`cursor-pointer transition-all ${
                  isSelected
                    ? 'border-amber-500 bg-amber-500/5 dark:bg-slate-900/90 shadow-md ring-1 ring-amber-500/30'
                    : 'border-border dark:border-slate-800 hover:border-slate-400 dark:hover:border-slate-700 bg-card dark:bg-slate-950/40'
                }`}
              >
                <CardHeader className="p-5">
                  <div className="flex items-start justify-between gap-2">
                    <CardTitle className="text-base font-semibold text-foreground dark:text-white">
                      {category.name}
                    </CardTitle>
                    <Badge variant="outline" className="text-[10px] font-mono shrink-0">
                      {category.questionCount || 0} Qs
                    </Badge>
                  </div>
                  {category.description && (
                    <CardDescription className="text-xs text-muted-foreground mt-2 leading-relaxed">
                      {category.description}
                    </CardDescription>
                  )}
                </CardHeader>
                <CardContent className="p-5 pt-0 flex justify-end gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      onStartStudy(trackData.track, category.id);
                    }}
                    className="text-xs text-amber-600 dark:text-amber-400 hover:bg-amber-500/10"
                  >
                    Practice This Topic
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
}
